import { useState } from 'react';
import { filesApi, type FileCategory } from '@/api/files';

/**
 * Companion hook for <PhotoUpload mode="staged">. Holds a pending File picked
 * by the user (without uploading it) and exposes commit() to upload it during
 * form submission.
 *
 * Usage:
 *   const photo = usePhotoUpload('student-photos');
 *
 *   const onFinish = async (values) => {
 *     const path = await photo.commit();          // string | undefined
 *     if (path !== undefined) values.photoPath = path;   // user picked a new file
 *     create.mutate(values);
 *   };
 *
 *   <Form.Item label="Photo" name="photoPath">
 *     <PhotoUpload category="student-photos" onPendingChange={photo.onPendingChange} />
 *   </Form.Item>
 */
export function usePhotoUpload(category: FileCategory) {
  const [pending, setPending] = useState<File | null>(null);

  return {
    /** Wire to <PhotoUpload onPendingChange={…} />. */
    onPendingChange: setPending,
    hasPending: pending != null,

    /**
     * Upload the pending file (if any). Returns:
     *   - the new stored path when a file was uploaded
     *   - undefined when nothing changed (caller should keep the form's existing photoPath)
     */
    commit: async (): Promise<string | undefined> => {
      if (!pending) return undefined;
      const result = await filesApi.upload(pending, category);
      setPending(null);
      return result.path;
    },

    /** Discard any pending file without uploading. */
    cancel: () => setPending(null),
  };
}
